import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../assets/icons/PenFilled';
import PenOutlineIcon from '../../../../assets/icons/PenOutline';
import useSidebarContext from '../../../../hooks/sidebar/useSidebarContext';
import { useFootprintAndProduct } from '../../../../hooks/useFootprintAndProduct';
import CustomIcon from '../../../../shared/components/custom-icon/CustomIcon';
import EmissionValueWithSuffix from '../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { EmissionSymbol } from '../../../../shared/interfaces/IFootprint';

const EmissionsSection = (): JSX.Element => {
  const { t } = useTranslation();
  const { openSidebar } = useSidebarContext();
  const {
    footprintDetails,
    isFootprintLoading: isLoading,
    showEditButton,
  } = useFootprintAndProduct();

  return (
    <div className='flex flex-column gap-2 w-full'>
      {/* Emissions */}
      <div className='flex flex-row gap-3 align-items-center pb-3'>
        <h4 className='m-0 p-0 font-semibold'>
          {t('footprintDetailsPage.sections.emissionsSection.title')}
        </h4>
        {showEditButton && (
          <CustomIcon
            icon={PenOutlineIcon}
            hoverIcon={PenFilledIcon}
            showTextOnHover
            hoverText={t('global.edit')}
            hoverTextColor='primary'
            className='cursor-pointer flex justify-content-center align-items-center'
            onClick={() => {
              openSidebar('edit-emissions-details-sidebar', {
                position: 'right',
                footprintDetails: footprintDetails,
              });
            }}
          />
        )}
      </div>

      <div className='flex flex-column gap-4'>
        {/* Fossil Emissions */}
        <div className='flex flex-column gap-2 pb-3'>
          <h5 className='m-0 p-0 font-semibold'>
            {t('footprintDetailsPage.sections.emissionsSection.fossilEmissionsTitle')}
          </h5>
          {/* Fossil Ghg Emissions */}
          <div className='flex flex-column gap-2'>
            <span className='font-medium'>
              {t('footprintDetailsPage.sections.emissionsSection.fossilGhgEmissions')}
            </span>
            {isLoading ? (
              <Skeleton height='16px' />
            ) : (
              <EmissionValueWithSuffix
                emissionValue={footprintDetails?.pcf?.fossil_ghg_emissions}
                emissionSymbol={EmissionSymbol.KG_CO2E}
                declaredUnit={footprintDetails?.pcf?.declared_unit}
                abbreviateUnit
                unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
                suffixTextClassName='text-black-alpha-60'
                numericalValueTextClassName='font-bold text-black-alpha-60'
              />
            )}
          </div>
        </div>

        {/* Carbon Content */}
        <div className='flex flex-column gap-2 pb-3'>
          <h5 className='m-0 p-0 font-semibold'>
            {t('footprintDetailsPage.sections.emissionsSection.carbonContentTitle')}
          </h5>
          <div className='flex flex-column md:flex-row gap-3 md:gap-6 w-full'>
            {/* Fossil Carbon Content */}
            <div className='flex flex-column gap-2'>
              <span className='font-medium'>
                {t('footprintDetailsPage.sections.emissionsSection.fossilCarbonContent')}
              </span>
              {isLoading ? (
                <Skeleton height='16px' />
              ) : (
                <EmissionValueWithSuffix
                  emissionValue={footprintDetails?.pcf?.fossil_carbon_content}
                  emissionSymbol={EmissionSymbol.KG_C}
                  declaredUnit={footprintDetails?.pcf?.declared_unit}
                  abbreviateUnit
                  unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
                  suffixTextClassName='text-black-alpha-60'
                  numericalValueTextClassName='font-bold text-black-alpha-60'
                />
              )}
            </div>

            {/* Biogenic Carbon Content */}
            <div className='flex flex-column gap-2'>
              <span className='font-medium'>
                {t('footprintDetailsPage.sections.emissionsSection.biogenicCarbonContent')}
              </span>
              {isLoading ? (
                <Skeleton height='16px' />
              ) : (
                <EmissionValueWithSuffix
                  emissionValue={footprintDetails?.pcf?.carbon_content_biogenic}
                  emissionSymbol={EmissionSymbol.KG_C}
                  declaredUnit={footprintDetails?.pcf?.declared_unit}
                  abbreviateUnit
                  unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
                  suffixTextClassName='text-black-alpha-60'
                  numericalValueTextClassName='font-bold text-black-alpha-60'
                />
              )}
            </div>
          </div>
        </div>

        {/* Total */}
        <div className='flex flex-column gap-2'>
          <h5 className='m-0 p-0 font-semibold'>
            {t('footprintDetailsPage.sections.emissionsSection.totalTitle')}
          </h5>
          <div className='flex flex-column gap-3'>
            {/* Pcf excl */}
            <div className='flex flex-column gap-2'>
              <span className='font-medium text-primary'>
                {t(
                  'footprintDetailsPage.sections.emissionsSection.pcfExclBiogenicEmissionsAndRemovals',
                )}
              </span>
              {isLoading ? (
                <Skeleton height='16px' />
              ) : (
                <EmissionValueWithSuffix
                  emissionValue={footprintDetails?.pcf?.pcf_excluding_biogenic}
                  emissionSymbol={EmissionSymbol.KG_CO2E}
                  declaredUnit={footprintDetails?.pcf?.declared_unit}
                  abbreviateUnit
                  unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
                  suffixTextClassName='text-primary'
                  numericalValueTextClassName='font-bold text-primary'
                />
              )}
            </div>
            {/* Pcf incl */}
            <div className='flex flex-column gap-2'>
              <span className='font-medium'>
                {t(
                  'footprintDetailsPage.sections.emissionsSection.pcfInclBiogenicEmissionsAndRemovals',
                )}
              </span>
              {isLoading ? (
                <Skeleton height='16px' />
              ) : (
                <EmissionValueWithSuffix
                  emissionValue={footprintDetails?.pcf?.pcf_including_biogenic}
                  emissionSymbol={EmissionSymbol.KG_CO2E}
                  declaredUnit={footprintDetails?.pcf?.declared_unit}
                  abbreviateUnit
                  unitaryProductAmount={footprintDetails?.pcf?.unitary_product_amount}
                  suffixTextClassName='text-black-alpha-60'
                  numericalValueTextClassName='font-bold text-black-alpha-60'
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmissionsSection;
